/**
 * Admin Notifications — Central de Alertas de Infraestrutura
 * 17 event types: provisioning, billing, security, business
 */

import { useState, useEffect, useCallback } from 'preact/hooks';
import { api } from '../api/client';
import { SkeletonBar, Button } from '../components/ui';
import { T } from '../styles/tokens';

const C = {
  bg: T.bg, card: T.bgInput, cardHover: T.bgSecondary,
  border: T.border, blue: T.primary,
  text: T.text, muted: T.textSecondary,
  font: "'Manrope', system-ui, sans-serif",
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: T.danger, error: T.warning, warning: T.warning, info: T.primary,
};

const SEVERITY_LABELS: Record<string, string> = {
  critical: 'Critico', error: 'Erro', warning: 'Aviso', info: 'Info',
};

const TYPE_LABELS: Record<string, string> = {
  server_provision_failed: 'Provisioning Falhou',
  openclaw_install_failed: 'Install Falhou',
  runner_offline: 'Runner Offline',
  vm_heartbeat_timeout: 'VM Heartbeat Timeout',
  orchestrator_error: 'Erro Orchestrator',
  ssl_expiring: 'SSL Expirando',
  payment_failed: 'Pagamento Falhou',
  rate_limit_hit: 'Rate Limit',
  budget_exceeded: 'Budget Excedido',
  new_subscription: 'Nova Assinatura',
  new_user: 'Novo Usuario',
  payment_received: 'Pagamento Recebido',
  subscription_cancelled: 'Assinatura Cancelada',
  machine_high_usage: 'Alta Utilizacao',
  suspicious_login: 'Login Suspeito',
  login_attempts_failed: 'Tentativas de Login',
  domain_blocked: 'Dominio Bloqueado',
};

interface Notification {
  id: string;
  type: string;
  severity: string;
  title: string;
  message: string | null;
  metadata: string | null;
  is_read: number;
  created_at: string;
  read_at: string | null;
  source: string | null;
  customer_id: string | null;
  machine_id: string | null;
}

export function AdminNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const LIMIT = 30;

  const fetchNotifications = useCallback(async (reset = false) => {
    try {
      setLoading(true);
      const currentOffset = reset ? 0 : offset;
      const params = new URLSearchParams({ limit: String(LIMIT), offset: String(currentOffset) });
      if (severityFilter) params.set('severity', severityFilter);
      if (typeFilter) params.set('type', typeFilter);

      const res = await api.get(`/admin/notifications?${params}`) as any;
      const data = res?.data || res;
      if (reset) {
        setNotifications(data.notifications || []);
        setOffset(0);
      } else if (currentOffset > 0) {
        setNotifications(prev => [...prev, ...(data.notifications || [])]);
      } else {
        setNotifications(data.notifications || []);
      }
      setTotal(data.total || 0);
    } catch (e) {
      console.error('Failed to load notifications', e);
    } finally {
      setLoading(false);
    }
  }, [severityFilter, typeFilter, offset]);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await api.get('/admin/notifications/unread-count') as any;
      setUnreadCount(res?.data?.count ?? res?.count ?? 0);
    } catch {}
  }, []);

  useEffect(() => { fetchNotifications(true); }, [severityFilter, typeFilter]);
  useEffect(() => { fetchUnread(); }, []);

  const markRead = async (id: string) => {
    try {
      await api.patch(`/admin/notifications/${id}/read`, {});
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: 1, read_at: new Date().toISOString() } : n));
      setUnreadCount(c => Math.max(0, c - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.patch('/admin/notifications/read-all', {});
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1, read_at: new Date().toISOString() })));
      setUnreadCount(0);
    } catch {}
  };

  const loadMore = () => {
    const newOffset = offset + LIMIT;
    setOffset(newOffset);
    // fetch with new offset
    const params = new URLSearchParams({ limit: String(LIMIT), offset: String(newOffset) });
    if (severityFilter) params.set('severity', severityFilter);
    if (typeFilter) params.set('type', typeFilter);
    api.get(`/admin/notifications?${params}`).then((res: any) => {
      const data = res?.data || res;
      setNotifications(prev => [...prev, ...(data.notifications || [])]);
    }).catch(() => {});
  };

  const timeAgo = (iso: string) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'agora';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `${days}d`;
  };

  const allTypes = Object.keys(TYPE_LABELS);

  return (
    <div style={{ padding: '32px 24px', maxWidth: 960, margin: '0 auto', fontFamily: C.font, color: C.text }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0 }}>Notificacoes</h1>
          {unreadCount > 0 && (
            <span style={{
              background: T.danger, color: T.text, fontSize: 12, fontWeight: 700,
              padding: '2px 8px', borderRadius: 12, minWidth: 20, textAlign: 'center',
            }}>{unreadCount}</span>
          )}
        </div>
        <Button variant="secondary" size="sm" onClick={markAllRead}>
          Marcar tudo como lido
        </Button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {['', 'critical', 'error', 'warning', 'info'].map(s => (
            <Button variant="ghost"
              key={s}
              onClick={() => setSeverityFilter(s)}
              style={{
                padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: severityFilter === s ? `1px solid ${s ? SEVERITY_COLORS[s] : C.blue}` : `1px solid ${C.border}`,
                background: severityFilter === s ? `${s ? SEVERITY_COLORS[s] : C.blue}22` : 'transparent',
                color: severityFilter === s ? (s ? SEVERITY_COLORS[s] : C.blue) : C.muted,
              }}
            >
              {s ? SEVERITY_LABELS[s] : 'Todos'}
            </Button>
          ))}
        </div>
        <select
          value={typeFilter}
          onChange={(e: any) => setTypeFilter(e.target.value)}
          style={{
            padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 500,
            background: C.card, color: C.text, border: `1px solid ${C.border}`,
            cursor: 'pointer',
          }}
        >
          <option value="">Todos os tipos</option>
          {allTypes.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
        </select>
      </div>

      {/* List */}
      {loading && notifications.length === 0 ? (
        <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>
      ) : notifications.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: 60, color: C.muted,
          background: C.card, borderRadius: 12, border: `1px solid ${C.border}`,
        }}>
          Nenhuma notificacao encontrada
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {notifications.map(n => {
            const sevColor = SEVERITY_COLORS[n.severity] || C.blue;
            const expanded = expandedId === n.id;
            let parsedMeta: any = null;
            if (n.metadata) { try { parsedMeta = JSON.parse(n.metadata); } catch {} }

            return (
              <div
                key={n.id}
                onClick={() => {
                  setExpandedId(expanded ? null : n.id);
                  if (!n.is_read) markRead(n.id);
                }}
                style={{
                  background: n.is_read ? C.card : `${C.card}`,
                  border: `1px solid ${n.is_read ? C.border : sevColor + '44'}`,
                  borderRadius: 10, padding: '14px 16px', cursor: 'pointer',
                  borderLeft: `3px solid ${sevColor}`,
                  opacity: n.is_read ? 0.7 : 1,
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                        color: sevColor, letterSpacing: '0.5px',
                      }}>{SEVERITY_LABELS[n.severity] || n.severity}</span>
                      <span style={{ fontSize: 10, color: C.muted }}>
                        {TYPE_LABELS[n.type] || n.type}
                      </span>
                      {!n.is_read && (
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: sevColor, flexShrink: 0 }} />
                      )}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{n.title}</div>
                    {n.message && (
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 4, lineHeight: 1.4 }}>
                        {n.message.length > 120 && !expanded ? n.message.slice(0, 120) + '...' : n.message}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {timeAgo(n.created_at)}
                  </div>
                </div>

                {/* Expanded details */}
                {expanded && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 11, color: C.muted, marginBottom: 8 }}>
                      {n.source && <span>Source: {n.source}</span>}
                      {n.customer_id && <span>Customer: {n.customer_id.slice(0, 8)}...</span>}
                      {n.machine_id && <span>Machine: {n.machine_id.slice(0, 8)}...</span>}
                      <span>{new Date(n.created_at).toLocaleString('pt-BR')}</span>
                    </div>
                    {parsedMeta && (
                      <pre style={{
                        background: T.bg, border: `1px solid ${C.border}`, borderRadius: 8,
                        padding: 12, fontSize: 11, color: C.muted, overflow: 'auto',
                        maxHeight: 200, margin: 0,
                      }}>
                        {JSON.stringify(parsedMeta, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Load more */}
          {notifications.length < total && (
            <Button variant="ghost" onClick={(e: any) => { e.stopPropagation(); loadMore(); }} style={{ marginTop: 8, width: '100%' }}>
              Carregar mais ({notifications.length}/{total})
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
